import { QueryObserverResult, useQuery } from '@tanstack/react-query'
import { constructFormAddressString } from 'app/views/forms/common/Common'
import { checkFormValidityBase } from 'app/views/forms/formHelpersValidation'
import { GRANTS_PATH } from 'app/views/grants/GrantsConstants'
import { RootStateOrAny, useSelector } from 'react-redux'
import { useForm } from './useForm'

interface UseOrganizationValidityBaseProps {
  accountId: string
  userId?: string
  formId?: string
}

interface refetchWithFreshDataResult {
  isValid: boolean
  error: unknown
}

interface UseOrganizationValidityBaseResult {
  isValid: boolean
  isLoadedAndInvalid: boolean
  error: unknown
  isLoading: boolean
  refetch: () => Promise<QueryObserverResult<boolean, unknown>>
  refetchWithFreshData: () => Promise<refetchWithFreshDataResult>
}

/**
 * Base hook for checking organization validity
 */
export const useOrganizationValidityBase = (props: UseOrganizationValidityBaseProps): UseOrganizationValidityBaseResult => {
  const {
    accountId,
    userId,
    formId
  } = props

  const { data: form, isLoading: isLoadingForm, error: formError, refetch: refetchForm } = useForm({ formId })
  const organization = useSelector((state: RootStateOrAny) => state.organization)

  const { data: validityResult, isLoading: isLoadingValidity, error: validityError, refetch } = useQuery({
    queryKey: ['form-validity', formId, accountId, userId],
    queryFn: () => {
      if (formId === undefined) {
        throw new Error('formId is required')
      }
      const isValid = checkFormValidityBase({
        form,
        targetFormUrl: constructFormAddressString({
          ids: {
            Account: accountId,
            User: userId
          },
          objectsConnected: form?.objectsConnected
        }),
        pathname: GRANTS_PATH.ORGANIZATION_DETAILS,
        accountId
      })

      return isValid
    },
    enabled: Boolean(form) && Boolean(organization?.id),
    refetchInterval: 1000 * 60 * 2,
    staleTime: 1000 * 60 * 2
  })

  const refetchWithFreshData = async (): Promise<refetchWithFreshDataResult> => {
    const formResult = await refetchForm()
    const freshForm = formResult.data

    const validityResult = await refetch()
    const validityError = validityResult.error

    if (formId === undefined || freshForm === undefined) {
      return {
        isValid: false,
        error: validityError ?? formResult.error
      }
    }

    const freshIsValid = await checkFormValidityBase({
      form: freshForm,
      targetFormUrl: constructFormAddressString({
        ids: {
          Account: accountId,
          User: userId
        },
        objectsConnected: freshForm?.objectsConnected
      }),
      pathname: '/grants/organization-details',
      accountId
    })

    // You can add additional data here if needed
    return {
      isValid: Boolean(freshIsValid),
      error: validityError ?? formResult.error
    }
  }

  const isValid = form !== undefined && Boolean(validityResult)
  const isLoadedAndInvalid = Boolean(validityError) || Boolean(formError) || (!isValid && (form !== undefined ? true : !isLoadingForm) && !isLoadingValidity)

  return {
    isValid,
    isLoadedAndInvalid,
    error: validityError ?? formError,
    isLoading: isLoadingForm || isLoadingValidity,
    refetch,
    refetchWithFreshData
  }
}
